import {
  Schedule,
  ScheduleEventData,
  ScheduleViewLevel,
} from "@mantine/schedule"
import {
  CalendarAppointment,
  useGetHolidaysForDateRange,
  useGetMyEventsForDateRange,
  useGetSchoolHolidaysForDateRange,
} from "@/orval/gen"
import dayjs from "dayjs"
import { dayJstoDateOnly } from "../../../../../utils/dayjs-to-date-only"
import { useEffect, useState } from "react"
import { useGetScheduleLabels } from "./useGetScheduleLabels"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { useColorSchemeSystemColors } from "@components/use-system-colors"
import styles from "./index.module.css"

interface Props {
  startDate: Date
  view: "day" | "week" | "month"
  setView: (view: "day" | "week" | "month" | "year", date?: Date) => void
  onDateChange?: (date: Date) => void
  showEndTime?: boolean
  showHolidays?: boolean
  showSchoolHolidays?: boolean
}

interface ScheduleAppointmentPayload {
  appointment?: CalendarAppointment
}

function toScheduleEvent(
  dateString: string,
  appointment: CalendarAppointment,
  index: number,
  tFunction: TFunction<"app_server">,
  showEndTime?: boolean
): ScheduleEventData<ScheduleAppointmentPayload> {
  const date = dayjs(dateString)
  const start = appointment.start
    ? `${date.format("YYYY-MM-DD")} ${appointment.start}`
    : `${date.format("YYYY-MM-DD")} 00:00:00`
  const isMultiDay =
    appointment.start && appointment.end && appointment.end < appointment.start
  const endDate = isMultiDay ? date.add(1, "day") : date
  const end = appointment.end
    ? `${endDate.format("YYYY-MM-DD")} ${appointment.end}`
    : `${endDate.format("YYYY-MM-DD")} 23:59:59`

  let title = ""
  title += appointment.start ? `${appointment.start.slice(0, 5)}` : ""
  title +=
    showEndTime && appointment.end ? `-${appointment.end.slice(0, 5)}` : ""
  title += " " + appointment.title
  let variant: "filled" | "light" = "filled"
  if (appointment.isLikeRequest) {
    title += ` (${tFunction("Requested")})`
    variant = "light"
  }

  return {
    id: `${appointment.source}-${appointment.id ?? index}-${date}`,
    title,
    start,
    end,
    color: appointment.backColorHEX || "gray",
    variant,
    payload: { appointment },
  }
}

function splitTwoDayEvent(events: ScheduleEventData[]): ScheduleEventData[] {
  const result: ScheduleEventData[] = []

  events.forEach((event) => {
    const start = dayjs(event.start)
    const end = dayjs(event.end)

    if (start.isSame(end, "day")) {
      result.push(event)
      return
    }

    result.push({
      ...event,
      id: `${event.id}-d1`,
      start: start.format("YYYY-MM-DD HH:mm:ss"),
      end: start.format("YYYY-MM-DD 23:59:59"),
    })

    result.push({
      ...event,
      id: `${event.id}-d2`,
      start: end.format("YYYY-MM-DD 00:00:00"),
      end: end.format("YYYY-MM-DD HH:mm:ss"),
    })
  })

  return result
}

function isDateAppointmentTuple(
  value: unknown
): value is [string, CalendarAppointment[]] {
  return (
    Array.isArray(value) &&
    typeof value[0] === "string" &&
    Array.isArray(value[1])
  )
}

export function CalendarScheduler({
  startDate,
  view,
  setView,
  onDateChange,
  showEndTime,
  showHolidays,
  showSchoolHolidays,
}: Props) {
  const [date, setDate] = useState(startDate)
  const labels = useGetScheduleLabels()
  const { t } = useTranslation("app_server")

  useEffect(() => {
    setDate(startDate)
  }, [startDate])

  const range = {
    day: {
      start: dayjs(date).add(-1, "day").startOf("day"),
      end: dayjs(date).endOf("day"),
    },
    week: {
      start: dayjs(date).startOf("isoWeek"),
      end: dayjs(date).endOf("isoWeek"),
    },
    month: {
      start: dayjs(date).startOf("month"),
      end: dayjs(date).endOf("month"),
    },
    year: {
      start: dayjs(date).startOf("year"),
      end: dayjs(date).endOf("year"),
    },
  }[view]

  const eventQuery = useGetMyEventsForDateRange({
    endDate: dayJstoDateOnly(range.end),
    startDate: dayJstoDateOnly(range.start),
    method: "NoCount",
    withPossibleCasts: true,
    onlyReleasedDuties: false,
    withPersStatus: true,
  })

  const { data: holidays } = useGetHolidaysForDateRange(
    {
      startDate: range.start.format("YYYY-MM-DD"),
      endDate: range.end.format("YYYY-MM-DD"),
    },
    { query: { enabled: showHolidays } }
  )

  const { data: schoolHolidays } = useGetSchoolHolidaysForDateRange(
    {
      startDate: range.start.format("YYYY-MM-DD"),
      endDate: range.end.format("YYYY-MM-DD"),
    },
    { query: { enabled: showSchoolHolidays } }
  )

  const flatEvents =
    (eventQuery.data as unknown[] | undefined)?.flatMap((entry) =>
      isDateAppointmentTuple(entry)
        ? entry[1].map((appointment, index) =>
            toScheduleEvent(entry[0], appointment, index, t, showEndTime)
          )
        : []
    ) ?? []

  const processedEvents =
    view !== "month" ? splitTwoDayEvent(flatEvents) : flatEvents

  const colors = useColorSchemeSystemColors()

  const getDayColor = (date: string) => {
    const day = dayjs(date)
    if (day.isToday()) {
      return colors.Today
    }
    if (day.day() === 6) {
      return colors.Saturday
    }
    if (day.day() === 0) {
      return colors.Sunday
    }
    return undefined
  }

  const holidayEvents: ScheduleEventData<ScheduleAppointmentPayload>[] =
    holidays?.map((holiday) => ({
      id: `holiday-${holiday.holidayId}`,
      title: holiday.name,
      start: `${holiday.date} 00:00:00`,
      end: `${holiday.date} 23:59:59`,
      color: colors.Holiday
        ? colors.Holiday
        : "var(--mantine-color-red-filled)",
      variant: "filled",
      display: "background",
    })) ?? []

  const schoolHolidayEvents: ScheduleEventData<ScheduleAppointmentPayload>[] =
    schoolHolidays?.map((schoolHoliday) => ({
      id: `school-holiday-${schoolHoliday.name}-${schoolHoliday.startDate}`,
      title: schoolHoliday.name ?? "",
      start: `${schoolHoliday.startDate} 00:00:00`,
      end: `${schoolHoliday.endDate} 23:59:59`,
      color: colors.SchoolHoliday
        ? colors.SchoolHoliday
        : "var(--mantine-color-blue-filled)",
      variant: "filled",
      display: "background",
    })) ?? []

  const events = [
    ...(showSchoolHolidays ? schoolHolidayEvents : []),
    ...processedEvents,
    ...(showHolidays ? holidayEvents : []),
  ]

  const updateDate = (newDate: Date) => {
    setDate(newDate)
    onDateChange?.(newDate)
  }

  const drillInto = (newDate: string, newView: ScheduleViewLevel) => {
    const date = dayjs(newDate).toDate()
    updateDate(date)
    setView(newView, date)
  }

  const handleEventClick = (event: ScheduleEventData) => {
    const eventDate = dayjs(event.start).format("YYYY-MM-DD")

    if (view !== "day") {
      drillInto(eventDate, "day")
    }
  }

  const handleDayClick = (day: string) => {
    drillInto(day, "day")
  }

  const handleWeekClick = (weekStart: string) => {
    drillInto(weekStart, "week")
  }

  const { xs } = useBreakpoints()

  return (
    <Schedule
      h="100%"
      mih={0}
      w="100%"
      miw={0}
      view={view}
      onViewChange={(newView) => setView(newView, date)}
      date={dayjs(date).format("YYYY-MM-DD")}
      onDateChange={(newDate) => updateDate(dayjs(newDate).toDate())}
      events={events}
      onEventClick={handleEventClick}
      onDayClick={handleDayClick}
      monthViewProps={{
        withHeader: false,
        withWeekNumbers: !xs,
        onWeekNumberClick: handleWeekClick,
        scrollAreaProps: { scrollbars: "y" },
        styles: {
          monthView: {
            "--min-day-width": "0px",
            "--font-size": "var(--mantine-font-size-xs)",
          },
          monthViewDayLabel: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
        getDayProps: (date) => ({
          bg: getDayColor(date),
        }),
        highlightToday: false,
      }}
      weekViewProps={{
        startTime: "00:00:00",
        endTime: "23:59:59",
        intervalMinutes: 30,
        firstDayOfWeek: 1,
        slotHeight: 48,
        withHeader: false,
        scrollAreaProps: { h: "100%", w: "100%" },
        h: "100%",
        w: "100%",
        styles: {
          weekViewRoot: { height: "100%", width: "100%" },
          weekViewInner: { overflow: "visible" },
          weekViewSlotLabels: {
            position: "sticky",
            insetInlineStart: 0,
            zIndex: 3,
            backgroundColor: "var(--mantine-color-body)",
            boxShadow: "1px 0 0 var(--week-view-border-color)",
          },
          weekViewAllDaySlotsLabel: {
            position: "sticky",
            insetInlineStart: 0,
            zIndex: 3,
            backgroundColor: "var(--mantine-color-body)",
            boxShadow: "1px 0 0 var(--week-view-border-color)",
          },
        },
      }}

      labels={labels}
      dayViewProps={{
        withHeader: false,
        h: "100%",
        w: "100%",
        scrollAreaProps: { h: "100%", w: "100%" },
        styles: {
          dayViewAllDay: {
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "var(--mantine-color-body)",
            boxShadow: "0 1px 0 var(--day-view-border-color)",
          },
        },
        classNames: {
          dayViewSlotLabel: styles.dayViewSlotLabel,
        },
      }}
    />
  )
}
